import React, { useState, useRef } from "react";
import { Button, Icon, Table, Label, Message } from "semantic-ui-react";
import * as XLSX from "xlsx";
import { Employee } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite } from "../../../../contexts";
import { toast } from "react-toastify";
import "./EmployeeImportModal.scss";

const empController = new Employee();

const COLUMN_MAP = {
  nombre: "nombre", name: "nombre",
  apellido: "apellido", lastname: "apellido", last_name: "apellido", surname: "apellido",
  numero_legajo: "numero_legajo", legajo: "numero_legajo", file_number: "numero_legajo",
  dni: "dni", documento: "dni", document: "dni",
  cargo: "cargo", position: "cargo", puesto: "cargo", job_title: "cargo",
  tipo: "tipo", type: "tipo",
  fecha_ingreso: "fecha_ingreso", hire_date: "fecha_ingreso", start_date: "fecha_ingreso",
  fecha_egreso: "fecha_egreso", end_date: "fecha_egreso", termination_date: "fecha_egreso",
  observations: "observations", observaciones: "observations", notes: "observations",
};

function normalizeKey(key) {
  return key?.toString().toLowerCase().trim().replace(/\s+/g, "_");
}

function parseRows(raw) {
  return raw
    .map((row) => {
      const mapped = {};
      for (const [k, v] of Object.entries(row)) {
        const field = COLUMN_MAP[normalizeKey(k)];
        if (field && v !== undefined && v !== null && String(v).trim() !== "") {
          mapped[field] = String(v).trim();
        }
      }
      return mapped;
    })
    .filter((r) => Object.keys(r).length > 0);
}

function validateRow(row) {
  const missing = [];
  if (!row.nombre)        missing.push("nombre");
  if (!row.apellido)      missing.push("apellido");
  if (!row.numero_legajo) missing.push("numero_legajo");
  return missing;
}

export function EmployeeImportModal({ onClose, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const { site } = useSite();
  const t = (key) => translations[key] || key;
  const fileRef = useRef();

  const [rows,    setRows]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [fileErr, setFileErr] = useState("");

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileErr(""); setResult(null); setRows(null);

    const ext = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();

    if (ext === "json") {
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          const arr = Array.isArray(data) ? data : (data.employees || []);
          setRows(parseRows(arr));
        } catch {
          setFileErr(t("invalid_json_file"));
        }
      };
      reader.readAsText(file, "utf-8");
    } else if (ext === "csv") {
      reader.onload = (ev) => {
        try {
          const bytes = new Uint8Array(ev.target.result);
          let text;
          try {
            text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
          } catch {
            text = new TextDecoder("windows-1252").decode(bytes);
          }
          const wb = XLSX.read(text, { type: "string" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
          setRows(parseRows(raw));
        } catch {
          setFileErr(t("error_reading_csv"));
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (ev) => {
        try {
          const wb = XLSX.read(ev.target.result, { type: "array", codepage: 65001 });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
          setRows(parseRows(raw));
        } catch {
          setFileErr(t("error_reading_file"));
        }
      };
      reader.readAsArrayBuffer(file);
    }
    e.target.value = "";
  };

  const validRows   = rows?.filter((r) => validateRow(r).length === 0) || [];
  const invalidRows = rows?.filter((r) => validateRow(r).length > 0)   || [];

  const handleImport = async () => {
    if (!validRows.length) return;
    setLoading(true);
    try {
      const res = await empController.bulkImportEmployees(accessToken, validRows, site);
      setResult(res);
      onReload();
    } catch (err) {
      toast.error(err?.msg || t("error"));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="emp-import">
        <Message positive>
          <Message.Header>{t("import_completed")}</Message.Header>
          <p>
            {t("import_created")}: <strong>{result.created}</strong> &nbsp;|&nbsp;
            {t("import_updated")}: <strong>{result.updated}</strong>
          </p>
          {result.errors?.length > 0 && (
            <p>{t("import_rows_with_errors")}: <strong>{result.errors.length}</strong></p>
          )}
        </Message>
        <div className="emp-import__actions">
          <Button primary onClick={onClose}>{t("close")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-import">
      <div className="emp-import__info">
        <div className="emp-import__formats">
          <span>{t("accepted_formats")}:</span>
          {[".xlsx", ".xls", ".csv", ".json"].map((f) => (
            <Label key={f} size="mini" color="blue">{f}</Label>
          ))}
        </div>
        <div className="emp-import__columns">
          <p><strong>{t("required_columns")}:</strong> nombre, apellido, numero_legajo</p>
          <p><strong>{t("optional_columns")}:</strong> dni, cargo, tipo (permanente/eventual), fecha_ingreso, fecha_egreso, observations</p>
        </div>
      </div>

      <div className="emp-import__drop" onClick={() => fileRef.current?.click()}>
        <Icon name="cloud upload" size="big" />
        <p>{t("click_to_select_file")}</p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv,.json"
          style={{ display: "none" }}
          onChange={handleFile}
        />
      </div>

      {fileErr && <Message negative size="tiny"><p>{fileErr}</p></Message>}

      {rows !== null && (
        <>
          <div className="emp-import__summary">
            <Label color="green">{validRows.length} {t("import_valid")}</Label>
            {invalidRows.length > 0 && (
              <Label color="red">{invalidRows.length} {t("import_with_errors")}</Label>
            )}
          </div>

          <div className="emp-import__preview">
            <Table compact size="small" celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("record_number")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("first_name")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("last_name")}</Table.HeaderCell>
                  <Table.HeaderCell>DNI</Table.HeaderCell>
                  <Table.HeaderCell>{t("job_title")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("type")}</Table.HeaderCell>
                  <Table.HeaderCell></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.slice(0, 10).map((r, i) => {
                  const errs = validateRow(r);
                  return (
                    <Table.Row key={i} negative={errs.length > 0}>
                      <Table.Cell>{r.numero_legajo || <span className="emp-import__missing">—</span>}</Table.Cell>
                      <Table.Cell>{r.nombre        || <span className="emp-import__missing">—</span>}</Table.Cell>
                      <Table.Cell>{r.apellido      || <span className="emp-import__missing">—</span>}</Table.Cell>
                      <Table.Cell>{r.dni           || "-"}</Table.Cell>
                      <Table.Cell>{r.cargo         || "-"}</Table.Cell>
                      <Table.Cell>{r.tipo          || "permanente"}</Table.Cell>
                      <Table.Cell textAlign="center">
                        {errs.length > 0 && (
                          <Icon name="warning sign" color="red" title={`${t("missing_fields")}: ${errs.join(", ")}`} />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            {rows.length > 10 && (
              <p className="emp-import__more">{t("import_showing")} 10 {t("import_of")} {rows.length} {t("import_rows")}</p>
            )}
          </div>

          <div className="emp-import__actions">
            <Button onClick={onClose}>{t("cancel")}</Button>
            <Button
              primary
              loading={loading}
              disabled={!validRows.length || loading}
              onClick={handleImport}
            >
              <Icon name="upload" />
              {t("import_btn")} {validRows.length} {validRows.length !== 1 ? t("employees_plural") : t("employee")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
